import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, Image, StyleSheet, View } from 'react-native';
import { FocusableHighlight, StepStatus, TWCAccessibleContent, TWCStep } from '@components';
import { useRecoilValue } from 'recoil';
import { SECOND } from 'time-constants';

import { assets } from '@assets';
import { apiConfig, onboardingTestIDs, Theme } from '@constants';
import { helpers } from '@helpers';
import { useContextNodeHandle, useContextReference, useCountdown, useGetAuth0AccessToken } from '@hooks';
import { navigationService } from '@services';
import { screenReaderEnabledState, userSettingsState } from '@store';

import { RadialGradientBackground } from '../../../components/loading/RadialGradientBackground';
import { TWCText } from '../../../components/primitives/TWCText/TWCText';
import { isAndroid } from '../../../helpers/Platform';
import { accessibleCode, accessibleURL } from '../../../helpers/StringUtils';
import { FOCUS_NOW } from '../../../helpers/Tools';

const testIDs = onboardingTestIDs.deviceActivation;
const initialStatuses = [StepStatus.INCOMPLETE, StepStatus.INCOMPLETE, StepStatus.INCOMPLETE];

const DeviceActivationContentAPI = () => {
  const { deviceCode, expirationTime, userCode, registerDeviceUrl } = useGetAuth0AccessToken();
  const { dspCustomerToken } = useRecoilValue(userSettingsState);
  const signupURL = helpers.getSignupUrl();
  const accountSetupLink = (signupURL ?? apiConfig.WEB_SIGN_UP_URL)?.replace(/http(s)?:\/\//, '');
  const activateLink = registerDeviceUrl ?? apiConfig.REGISTER_DEVICE_URL?.replace(/http(s)?:\/\//, '');
  const [statuses, setStatuses] = useState(initialStatuses);

  useEffect(() => {
    const changed = [...initialStatuses];

    if (deviceCode) {
      changed[0] = StepStatus.COMPLETE;
    }
    if (dspCustomerToken) {
      changed[0] = StepStatus.COMPLETE;
      changed[1] = StepStatus.COMPLETE;
      changed[2] = StepStatus.COMPLETE;
    }
    setStatuses(changed);
  }, [deviceCode, dspCustomerToken]);

  useEffect(() => {
    if (statuses[2] === StepStatus.COMPLETE) {
      const timeout = setTimeout(() => navigationService.replace(onboardingTestIDs.pages.successPage), 2 * SECOND);

      return () => clearTimeout(timeout);
    }
  }, [statuses]);

  return (
    <DeviceActivationContentUI
      accountSetupLink={accountSetupLink}
      activateLink={activateLink}
      expirationTime={expirationTime}
      statuses={statuses}
      userCode={userCode}
    />
  );
};

const DeviceActivationContentUI = ({ activateLink, expirationTime, statuses, userCode }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'deviceActivationPage',
  });
  const { text: expiresIn, minutes } = useCountdown(expirationTime);

  const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);

  const accessibleUserCode = accessibleCode(userCode);
  const accessibleLink = accessibleURL(activateLink);

  const step1Reference = useContextReference(testIDs.firstStepSection);
  const step1Handle = useContextNodeHandle(testIDs.firstStepSection);
  const step2Handle = useContextNodeHandle(testIDs.secondStepSection);
  const step3Handle = useContextNodeHandle(testIDs.thirdStepSection);

  const focusTrapReference = useContextReference(testIDs.focusTrap);
  const focusTrapHandle = useContextNodeHandle(testIDs.focusTrap);
  const nextFocusTrap = isAndroid ? focusTrapHandle : null;

  useEffect(() => {
    if (screenReaderEnabled) {
      FOCUS_NOW(step1Reference);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenReaderEnabled]);

  return (
    <RadialGradientBackground>
      <View style={styles.pageContent}>
        <Image source={assets.twcLogo} style={styles.logo} />
        <TWCText
          importantForAccessibility={'no'}
          numberOfLines={1}
          style={styles.pageTitle}
          testID={testIDs.headingText}
          variant={'mediumText60'}
        >
          {t('headingText')}
        </TWCText>

        <View style={styles.steps}>
          {/* Step 1 */}
          <TWCAccessibleContent
            accessibilityHint={t('firstSectionAccessibilityHint')}
            accessibilityLabel={t('firstSectionAccessibilityLabel', {
              link: accessibleLink,
            })}
            disabledScreenreaderFocus={focusTrapHandle}
            nextFocusDown={step2Handle}
            testID={testIDs.firstStepSection}
          >
            <TWCStep status={statuses[0]} step={1} testID={testIDs.firstStep}>
              <View style={styles.verticalCenteredRow}>
                <TWCText style={styles.stepText} testID={testIDs.firstStepText} variant={'regularText44'}>
                  {t('firstStepText1')}{' '}
                  <TWCText noTestID style={styles.activateLink} variant={'regularText44'}>
                    {activateLink}
                  </TWCText>
                </TWCText>
              </View>
            </TWCStep>
          </TWCAccessibleContent>

          {/* Step 2 */}
          <TWCAccessibleContent
            accessibilityHint={t('secondSectionAccessibilityHint', { minutes })}
            accessibilityLabel={t('secondSectionAccessibilityLabel', {
              code: accessibleUserCode,
            })}
            disabledScreenreaderFocus={focusTrapHandle}
            nextFocusDown={step3Handle}
            nextFocusUp={step1Handle}
            testID={testIDs.secondStepSection}
            onPress={() => {
              AccessibilityInfo.announceForAccessibility(t('codeAnnouncement', { code: accessibleUserCode }));
            }}
          >
            <TWCStep status={statuses[1]} step={2} testID={testIDs.secondStep}>
              <View style={styles.verticalCenteredRow}>
                <TWCText style={styles.stepText} testID={testIDs.secondStepText} variant={'regularText44'}>
                  {t('secondStepText')}
                </TWCText>
                {Boolean(userCode) && (
                  <View style={styles.activationCodeContainer}>
                    <TWCText style={styles.activationCode} testID={testIDs.activationCode} variant={'regularText60'}>
                      {userCode}
                    </TWCText>
                    <TWCText style={styles.expirationText} testID={testIDs.expirationText} variant={'regularText26'}>
                      {statuses[1] === StepStatus.COMPLETE ? t('codeUsedText') : t('codeExpirationText', { expiresIn })}
                    </TWCText>
                  </View>
                )}
              </View>
            </TWCStep>
          </TWCAccessibleContent>

          {/* Step 3 */}
          <TWCAccessibleContent
            accessibilityHint={t('thirdSectionAccessibilityHint')}
            accessibilityLabel={t('thirdStepText')}
            disabledScreenreaderFocus={focusTrapHandle}
            nextFocusUp={step2Handle}
            testID={testIDs.thirdStepSection}
          >
            <TWCStep status={statuses[2]} step={3} testID={testIDs.thirdStep}>
              <View style={styles.verticalCenteredRow}>
                <TWCText style={styles.stepText} testID={testIDs.thirdStepText} variant={'regularText44'}>
                  {t('thirdStepText')}
                </TWCText>
              </View>
            </TWCStep>
          </TWCAccessibleContent>
        </View>
      </View>
      <FocusableHighlight
        importantForAccessibility={'no'}
        nextFocusDown={nextFocusTrap}
        nextFocusLeft={nextFocusTrap}
        nextFocusRight={nextFocusTrap}
        nextFocusUp={nextFocusTrap}
        ref={focusTrapReference}
        testID={testIDs.focusTrap}
      >
        <View />
      </FocusableHighlight>
    </RadialGradientBackground>
  );
};

export const DeviceActivationContent = {
  API: DeviceActivationContentAPI,
  UI: DeviceActivationContentUI,
};

const styles = StyleSheet.create({
  activateLink: {
    color: Theme.dodgerLightBlue,
  },
  activationCode: {
    color: Theme.dodgerLightBlue,
    letterSpacing: Theme.px(16),
    lineHeight: Theme.px(78),
    paddingLeft: Theme.px(16),
    textAlign: 'center',
  },
  activationCodeContainer: {
    alignItems: 'center',
    marginLeft: Theme.px(70),
  },
  expirationText: {
    lineHeight: Theme.px(34),
  },
  logo: {
    height: Theme.px(156),
    width: Theme.px(156),
  },
  pageContent: {
    alignItems: 'center',
    paddingVertical: Theme.px(77),
  },
  pageTitle: {
    lineHeight: Theme.px(79),
    marginBottom: Theme.px(41),
    marginTop: Theme.px(47),
  },
  stepText: {
    lineHeight: Theme.px(81),
  },
  steps: {
    paddingLeft: Theme.px(163),
  },
  verticalCenteredRow: {
    alignItems: 'center',
    flexDirection: 'row',
    maxWidth: Theme.px(1500),
    minHeight: Theme.px(113),
  },
});
